import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiHome, FiBriefcase, FiInfo, FiEdit,
  FiCheckSquare, FiFileText, FiPhone, FiMenu, FiX, FiArrowRight
} from "react-icons/fi";
import { FiGrid } from "react-icons/fi";

const NAV_ITEMS = [
  { id: "home", label: "Home", icon: FiHome, link: "/" },
  


  // ✅ ADMIN DASHBOARD
  { id: "dashboard", label: "Dashboard", icon: FiGrid, link: "/AdminLoginIndex" },

  { id: "official", label: "Official", icon: FiBriefcase, link: "https://dailygolive.in", external: true },
  { id: "about", label: "About Us", icon: FiInfo, link: "https://dailygolive.in/AboutUsIndex" },
  { id: "registration", label: "Registration", icon: FiEdit, link: "/Login-Form-identity" },
  { id: "attendance", label: "Attendance", icon: FiCheckSquare, link: "/AttendenceIndex" },
  { id: "bill", label: "Bill", icon: FiFileText, link: "/BillGeneratorLogin" },
  { id: "contact", label: "Contact", icon: FiPhone, link: "https://dailygolive.in/GetInTouchIndex" },
];


export default function LeftNavbar() {
  const [active, setActive] = useState("home");
  const [isOpen, setIsOpen] = useState(false);

  // Prevent scrolling when mobile menu is open
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "unset";
  }, [isOpen]);

  // Mobile Menu Stagger Animation Variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { x: -20, opacity: 0 },
    show: { x: 0, opacity: 1 },
  };

  return (
    <>
      {/* --- DESKTOP NAVIGATION --- */}
      <aside className="fixed left-6 top-1/2 -translate-y-1/2 z-50 hidden lg:block">
        <nav className="relative flex flex-col items-center gap-3 p-3 rounded-full bg-white/60 backdrop-blur-xl border border-white/50 shadow-[0_8px_32px_rgba(0,0,0,0.12)]">
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;

            return (
              <a
                key={item.id}
                href={item.link}
                onClick={() => setActive(item.id)}
                className="relative p-3 rounded-full group isolate"
                aria-label={item.label}
              >
                {/* Active Pill Background */}
                {isActive && (
                  <motion.div
                    layoutId="activePill"
                    className="absolute inset-0 bg-gradient-to-br from-gray-800 to-black rounded-full shadow-lg"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}

                {/* Hover Effect (Subtle Gray for non-active) */}
                {!isActive && (
                  <div className="absolute inset-0 rounded-full bg-gray-200/0 group-hover:bg-gray-200/50 transition-colors duration-300" />
                )}

                {/* Icon with Interaction */}
                <motion.div
                  whileHover={{ scale: 1.15 }}
                  whileTap={{ scale: 0.9 }}
                  className="relative z-10"
                >
                  <Icon
                    className={`text-xl transition-colors duration-300 ${
                      isActive ? "text-white" : "text-gray-500 group-hover:text-gray-900"
                    }`}
                  />
                </motion.div>

                {/* Enhanced Tooltip */}
                <div className="absolute left-full top-1/2 -translate-y-1/2 ml-4 px-2 py-1 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 pointer-events-none">
                  <div className="relative bg-gray-900/90 backdrop-blur-md text-white text-xs font-medium py-1.5 px-3 rounded-lg shadow-xl whitespace-nowrap">
                    {item.label}
                    {/* Tiny Arrow */}
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-2 h-2 bg-gray-900/90 rotate-45" />
                  </div>
                </div>
              </a>
            );
          })}
        </nav>
      </aside>

      {/* --- MOBILE HAMBURGER --- */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed top-5 left-5 z-50 lg:hidden p-3.5 bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-2xl text-gray-800"
      >
        <FiMenu size={24} />
      </motion.button>

      {/* --- MOBILE DRAWER --- */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] lg:hidden"
            />

            {/* Drawer Container */}
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
              className="fixed left-0 top-0 bottom-0 w-[85%] max-w-[320px] bg-white z-[70] shadow-2xl lg:hidden flex flex-col"
            >
              {/* Drawer Header */}
              <div className="p-6 flex justify-between items-center border-b border-gray-100 bg-gray-50/50">
                <div>
                    <h2 className="font-bold text-xl text-gray-900">Menu</h2>
                    <p className="text-xs text-gray-400 font-medium">DailyGoLive</p>
                </div>
                <motion.button 
                  whileHover={{ rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setIsOpen(false)} 
                  className="p-2 bg-white rounded-full shadow-sm text-gray-500 hover:text-red-500 transition-colors"
                >
                  <FiX size={20} />
                </motion.button>
              </div>

              {/* Drawer Links with Staggered Animation */}
              <motion.div 
                className="flex-1 overflow-y-auto p-4 space-y-2"
                variants={containerVariants}
                initial="hidden"
                animate="show"
              >
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = active === item.id;
                  
                  return (
                    <motion.a
                      key={item.id}
                      variants={itemVariants}
                      href={item.link}
                      onClick={() => {
                        setActive(item.id);
                        setIsOpen(false);
                      }}
                      className={`group flex items-center justify-between p-4 rounded-xl transition-all duration-300 border ${
                        isActive 
                          ? "bg-gray-900 border-gray-900 text-white shadow-lg shadow-gray-900/20" 
                          : "bg-white border-transparent hover:border-gray-100 text-gray-600 hover:bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${isActive ? "bg-white/20" : "bg-gray-100 text-gray-500 group-hover:text-gray-900"}`}>
                             <Icon size={20} />
                        </div>
                        <span className="font-semibold text-sm">{item.label}</span>
                      </div>
                      
                      {/* Little arrow hint on hover */}
                      {!isActive && (
                        <FiArrowRight className="opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all text-gray-400" />
                      )}
                    </motion.a>
                  );
                })}
              </motion.div>
              
              {/* Footer Area */}
              <div className="p-6 border-t border-gray-100 bg-gray-50/50">
                <p className="text-xs text-center text-gray-400">© 2024 DailyGoLive</p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}